import { makeRequest, dispatchAction, iamBaseHeader, logError } from '../../utils';
import { httpActions, appActions, authActions } from '../../actions';
import { config } from '../../config';
import { iamMessages } from '../../config';
import { setIdentity } from '../../lib/analytics/analytics-kissmetrics';

/**
 * handleHttpError
 * @desc helper method to handle 'true' HTTP errors
 * @param {object} error - error returned from http request
 * @param {string} requestName - reference for API call
 * @param {function} dispatch - Redux dispatch
 * @param {function} reject
 * @param {string} errorMessage - optional error message string to log error against in state tree
 * @param {function} _logError - optional function, defaults to imported logError, used for testing
 * @param {object} _iamMessages - optional object, defaults to imported iamMessages, used for testing
 * @returns {promise} reject - promise rejection
 */
export const handleHttpError = (error, requestName, dispatch, reject, errorMessage, _logError = logError, _iamMessages = iamMessages) => {
	if (error.message !== iamMessages.INVALID_CREDENTIALS.message) {
		_logError(error);
	}
	switch (error.message) {
		case 'Request timeout':
			dispatch(appActions.updateNetworkStatus(navigator.onLine));
			dispatch(httpActions.iamRequestError(requestName, 'iam_request_timeout'));
			break;
		default:
			dispatch(httpActions.iamRequestError(requestName, errorMessage));
	}
	return reject();
};

/**
 * parseHttpError
 * @desc helper method to parse HTTP errors
 * @param {object} error - error returned from http request
 * @returns {Promise.<T>} resolve with either the response body or an error
 */
export const parseHttpError = (error) => {
	if (error.response) {
		return error.response.json()
			.catch(err => {
				return Promise.resolve(err);
			});
	} else {
		return Promise.resolve(error);
	}
};

/**
 * iamEmailCheck
 * @desc Call the IAM Email check API with email address supplied by the user
 * If the user is found a success response is returned. In all other cases, some kind of HTTP error response is
 * returned. We inspect the error response for specific messages, which indicate a successful request execution
 * which we thus want to convert into a resolve of the outer promise. All other errors are assumed to be true HTTP
 * errors and are delegated to a helper function to deal with
 * @returns {Promise} Redux thunk promise
 */
export const iamEmailCheck = () => (dispatch, getState) => {
	return new Promise((resolve, reject) => {
		const requestName = 'iam_email_check';
		const email = getState().emailAddress;

		const url = config.iam.emailCheckUrl;
		const requestAction = dispatchAction(dispatch, httpActions.iamRequestMade(requestName));
		const requestBody = {
			'EmailAddress': email,
		};
		return makeRequest(url, requestAction, {
			method: 'POST',
			mode: 'cors',
			headers: iamBaseHeader,
			body: JSON.stringify(requestBody),
		})
		.then(() => {
			dispatch(httpActions.iamRequestSuccess(requestName));
			return resolve(iamMessages.USER_FOUND);
		})
		.catch(error => {
			return parseHttpError(error)
				.then(errorResponse => {
					if (errorResponse && errorResponse.Errors) {
						switch (errorResponse.Errors[0].ErrorMessage) {
							case iamMessages.NO_USER_FOUND.message:
								dispatch(httpActions.iamRequestSuccess(requestName));
								return resolve(iamMessages.NO_USER_FOUND.error);
							default:
								return handleHttpError(error, requestName, dispatch, reject, 'iam_email_check');
						}
					} else {
						return handleHttpError(error, requestName, dispatch, reject, 'iam_email_check');
					}
				});
		});
	});
};

/**
 * iamFacebookLogin
 * @desc Call the IAM Facebook login API with data collected from FB login.
 * Abort login and reject the promise if all required facebook data is not available.
 * @param {function} setKissmetricsIdentity used as an override for testing
 * @returns {Promise} Redux thunk promise
 */
export const iamFacebookLogin = (requestSettings, setKissmetricsIdentity = setIdentity) => (dispatch, getState) => {
	return new Promise((resolve, reject) => {
		const { ClientId, RegistrationSource, LongLived } = requestSettings;
		const requestName = 'iam_facebook_login';
		const firstName = getState().FACEBOOK_firstName;
		const lastName = getState().FACEBOOK_lastName;
		const accessToken = getState().FACEBOOK_accessToken;
		const email = getState().FACEBOOK_email;

		if (!firstName || !lastName || !accessToken || !email) {
			dispatch(httpActions.iamRequestError(requestName, 'insufficient_facebook_data'));
			return reject();
		}
		const url = config.iam.facebookLoginUrl;
		const requestAction = dispatchAction(dispatch, httpActions.iamRequestMade(requestName));
		const requestBody = {
			'FirstName': firstName,
			'LastName': lastName,
			'AccessToken': accessToken,
			'EmailAddress': email,
			'RegistrationSource': RegistrationSource,
			'LongLived': LongLived,
			'GrantType': 'facebook',
			'ClientId': ClientId,
		};

		return makeRequest(url, requestAction, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			headers: iamBaseHeader,
			body: JSON.stringify(requestBody),
		})
		.then(response => {
			const accessToken = response.Result.access_token;
			// send jwt [extract email] to kissmetrics
			setKissmetricsIdentity(accessToken);
			dispatch(authActions.updateAuthToken(accessToken));
			dispatch(httpActions.iamRequestSuccess(requestName));
			return resolve({
				accessToken: accessToken,
				refreshToken: response.Result.refresh_token,
			});
		})
		.catch(error => {
			handleHttpError(error, requestName, dispatch, reject, 'iam_jg_login');
		});
	});
};

/**
 * iamJgLogin
 * @desc Call the IAM 'JG credentials' login API with email address and password supplied by the user.
 * @param {function} setKissmetricsIdentity used as an override for testing
 * @returns {Promise} Redux thunk promise
 */
export const iamJgLogin = (requestSettings, setKissmetricsIdentity = setIdentity) => (dispatch, getState) => {
	return new Promise((resolve, reject) => {
		const { ClientId } = requestSettings;
		const requestName = 'iam_jg_login';
		const email = getState().emailAddress;
		const password = getState().password;
		const url = config.iam.jgLoginUrl;
		const requestAction = dispatchAction(dispatch, httpActions.iamRequestMade(requestName));
		const requestBody = {
			'Username': email,
			'Password': password,
			'GrantType': 'password',
			'ClientId': ClientId,
			'LongLived': true,
		};

		return makeRequest(url, requestAction, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			headers: iamBaseHeader,
			body: JSON.stringify(requestBody),
		})
		.then(response => {
			dispatch(httpActions.iamRequestSuccess(requestName));
			// send jwt [extract email] to kissmetrics
			setKissmetricsIdentity(response.Result.access_token);
			return resolve({
				accessToken: response.Result.access_token,
				refreshToken: response.Result.refresh_token,
			});
		})
		.catch(error => {
			return parseHttpError(error)
				.then( errorResponse => {
					let parsedError = error;
					if (errorResponse && errorResponse.Errors) {
						parsedError = new Error(errorResponse.Errors[0].ErrorMessage || 'No error message returned from IAM');
					}
					return handleHttpError(parsedError, requestName, dispatch, reject, 'iam_jg_login');
				});
		});
	});
};

/**
 * iamRegisterAndLogin
 * @desc Call the IAM Register and login API with data collected from register page
 * Abort register call and reject the promise if API returns an error status code.
 * @returns {Promise} Redux thunk promise
 */
export const iamRegisterAndLogin = (setKissmetricsIdentity = setIdentity) => (dispatch, getState) => {
	return new Promise((resolve, reject) => {
		const requestName = 'iam_register';

		const firstName = getState().firstName;
		const lastName = getState().lastName;
		const emailAddress = getState().emailAddress;
		const password = getState().password;

		if (!firstName || !lastName || !emailAddress || !password) {
			dispatch(httpActions.iamRequestError(requestName, 'insufficient_user_data'));
			return reject();
		}

		const url = config.iam.registerAndLoginUrl;
		const clientId = config.clientId;
		const requestAction = dispatchAction(dispatch, httpActions.iamRequestMade(requestName));
		const requestBody = {
			FirstName: firstName,
			LastName: lastName,
			EmailAddress: emailAddress,
			Password: password,
			GrantType: 'register_justgiving',
			ClientId: clientId,
			RegistrationSource: 2,
			LongLived: true,
		};

		return makeRequest(url, requestAction, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			headers: iamBaseHeader,
			body: JSON.stringify(requestBody),
		})
		.then(response => {
			dispatch(httpActions.iamRequestSuccess(requestName));
			// send jwt [extract email] to kissmetrics
			setKissmetricsIdentity(response.Result.access_token);
			return resolve({
				accessToken: response.Result.access_token,
				refreshToken: response.Result.refresh_token,
			});
		})
		.catch(error => {
			return parseHttpError(error)
				.then(errorResponse => {
					if (errorResponse && errorResponse.Errors) {
						switch (errorResponse.Errors[0].ErrorMessage) {
							case iamMessages.EMAIL_ADDRESS_IN_USE.message:
								dispatch(httpActions.iamRequestError(requestName, iamMessages.EMAIL_ADDRESS_IN_USE.error));
								return reject();
							default:
								return handleHttpError(error, requestName, dispatch, reject, 'iam_register');
						}
					} else {
						return handleHttpError(error, requestName, dispatch, reject, 'iam_register');
					}
				});
		});
	});
};



// WEBPACK FOOTER //
// ../~/@jg/justgiving-app-login/app/actions/thunks/iamThunk.js